import { useEffect, useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import { Globe, Check } from "lucide-react";

export default function Navigation() {
  const { t, lang, setLang, langs } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest("[data-lang-menu]")) setLangOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const links = [
    { id: "hero", label: t("nav.hero") },
    { id: "services", label: t("nav.services") },
    { id: "about", label: t("nav.about") },
    { id: "process", label: t("nav.process") },
    { id: "contact", label: t("nav.contact") },
  ];

  const currentLang = langs.find((l) => l.code === lang) || langs[0];

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div
          className={`flex items-center justify-between rounded-full transition-all duration-500 ${
            scrolled ? "glass px-4 py-2.5" : "px-2 py-2"
          }`}
        >
          <button
            data-testid="nav-logo"
            onClick={() => go("hero")}
            className="flex items-center gap-2.5 group"
          >
            <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl overflow-hidden bg-white shadow-[0_2px_10px_rgba(15,5,36,0.08)] border border-[#E5E3F0]">
              <img
                src="/logo.jpg"
                alt="MARCHUK LLC"
                className="h-full w-full object-contain p-1"
                loading="eager"
              />
            </span>
            <span className="font-display text-[15px] font-semibold tracking-tight text-[#0F0524] hidden sm:inline">
              MARCHUK <span className="font-light opacity-60">LLC</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="px-4 py-2 rounded-full text-[13px] font-medium text-[#5C527A] hover:text-[#0F0524] hover:bg-white/60 transition-all"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative" data-lang-menu>
              <button
                data-testid="lang-switcher-btn"
                onClick={() => setLangOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-full glass px-3 py-2 text-[12px] font-semibold text-[#0F0524] hover:bg-white/80 transition"
                aria-label="Language"
              >
                <Globe className="h-3.5 w-3.5 text-[#A49BFF]" />
                <span className="tracking-wider">{currentLang.short}</span>
              </button>
              {langOpen && (
                <div
                  data-testid="lang-menu"
                  className="absolute right-0 mt-2 w-40 glass rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(15,5,36,0.25)] border border-white/60 z-50"
                >
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] text-left hover:bg-white/70 transition ${
                        l.code === lang ? "text-[#0F0524] font-semibold" : "text-[#5C527A]"
                      }`}
                    >
                      <span>{l.label}</span>
                      {l.code === lang && <Check className="h-3.5 w-3.5 text-[#A49BFF]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="nav-cta-contact"
              onClick={() => go("contact")}
              className="hidden md:inline-flex btn-sheen relative items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}
            >
              {t("nav.cta")}
              <span className="inline-block h-1.5 w-1.5 rounded-full iridescent" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden glass h-10 w-10 rounded-full grid place-items-center"
              aria-label="Menu"
            >
              <div className="flex flex-col gap-1">
                <span className={`h-[1.5px] w-4 bg-[#0F0524] transition-all ${open ? "translate-y-[3px] rotate-45" : ""}`} />
                <span className={`h-[1.5px] w-4 bg-[#0F0524] transition-all ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
              </div>
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="md:hidden mt-3 glass rounded-3xl p-4 flex flex-col">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-left px-4 py-3 rounded-2xl text-[14px] font-medium text-[#0F0524] hover:bg-white/60"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("contact")}
              className="mt-2 rounded-2xl px-4 py-3 text-[14px] font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}
            >
              {t("nav.cta")}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
